/** ColumnsMenu — toolbar menu to show/hide columns and reset the column layout. */
import React, { useState } from 'react'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import Checkbox from '@mui/material/Checkbox'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'

export interface ColumnsMenuProps {
  columns: { name: string; title: string }[]
  hidden: string[]
  onToggle: (name: string) => void
  onReset: () => void
  /** Command-bar slot: icon + tooltip. More keeps the labeled button. */
  iconOnly?: boolean
  /** When set, the trigger calls onOpen and no Menu is rendered. */
  onOpen?: () => void
}

export const ColumnsMenu: React.FC<ColumnsMenuProps> = ({
  columns,
  hidden,
  onToggle,
  onReset,
  iconOnly = false,
  onOpen,
}) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const visibleCount = columns.filter((c) => !hidden.includes(c.name)).length
  const handleTrigger = (e: React.MouseEvent<HTMLElement>) => {
    if (onOpen) {
      onOpen()
      return
    }
    setAnchor(e.currentTarget)
  }
  const trigger = iconOnly ? (
    <Tooltip title="Columns">
      <IconButton size="small" aria-label="Columns" onClick={handleTrigger}>
        <ViewColumnIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : (
    <Button size="small" startIcon={<ViewColumnIcon />} onClick={handleTrigger} sx={{ textTransform: 'none', fontSize: 12 }}>Columns</Button>
  )
  if (onOpen) {
    return trigger
  }
  return (
    <>
      {trigger}
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {columns.map((c) => {
          const shown = !hidden.includes(c.name)
          const lastVisible = shown && visibleCount === 1
          return (
            <MenuItem key={c.name} disabled={lastVisible} onClick={() => { if (!lastVisible) onToggle(c.name) }}>
              <Checkbox size="small" checked={shown} />
              <ListItemText primary={c.title} />
            </MenuItem>
          )
        })}
        <Divider />
        <MenuItem onClick={() => { onReset(); setAnchor(null) }}>Reset columns</MenuItem>
      </Menu>
    </>
  )
}
